import { IModel, IAttribute, IViolation } from '../base/annotation';
import { LinkedService } from '../linkedservice/linkedservice';
import { SqlAdditionalPropertiesKeyType, SqlEncryptType, SqlVersionType } from '../../constants/sql-constants';
import { ParameterConfig } from '../pipeline/parameter-config';
import { Credential } from '../credential/credential';
import { Metamodel } from '../base/model';
import { ModelConstants } from '../model-constants';
import { format } from '../../shared/utils/string.util';
import { AzureKeyVault } from './key-vault';
import { validateAKVLinkedServiceReference } from '../../shared/utils/linkedservice-validation-util';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { SqlServerAuthType } from '../../interfaces/IAuthenticationType';

@Metamodel.model({
    name: ModelConstants.sqlServerLinkedService,
    display: [
        format(shellStringResources.ModelName_LinkedServiceTemplate, shellStringResources.SqlServer_DisplayText),
        format(shellStringResources.ModelName_LinkedServicesTemplate, shellStringResources.SqlServer_DisplayText)
    ]
})
export class SqlServerLinkedService extends LinkedService {

    public static readonly MODEL: IModel;
    public static readonly SERVER_NAME: IAttribute;
    public static readonly DATABASE_NAME: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly ENCRYPTION_SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly ENCRYPTION_SERVICE_PRINCIPAL_KEY: IAttribute;
    public static readonly ENCRYPT: IAttribute;
    public static readonly TRUST_SERVER_CERTIFICATE: IAttribute;
    public static readonly HOST_NAME_IN_CERTIFICATE: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServerName,
        association: 'SERVER_NAME',
        templatable: true
    })
    public serverName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_DatabaseName,
        association: 'DATABASE_NAME',
        templatable: true
    })
    public databaseName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public userName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Password,
        constraint: {
            validator: validateAKVLinkedServiceReference
        },
        association: 'PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AuthenticationType
    })
    public authenticationType?: SqlServerAuthType;

    public enableAKV?: boolean = false;
    @Metamodel.attribute({
        constraint: {
            validator: validateAKVLinkedServiceReference
        }
    })
    public referedAKV?: AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.AlwaysEncrypted
    })
    public alwaysEncrypted?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.KeyStoreAuthType
    })
    public keyStoreAuthType: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalID,
        association: 'ENCRYPTION_SERVICE_PRINCIPAL_ID',
        templatable: true
    })
    public encryptionServicePrincipalId: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalKey,
        constraint: {
            validator: validateAKVLinkedServiceReference
        },
        association: 'ENCRYPTION_SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String']
    })
    public encryptionServicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.Credentials,
        reference: true
    })
    public alwaysEncryptionCredential?: Credential;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: SqlServerLinkedService.validateCredential
        }
    })
    public credential?: Credential;

    public sqlVersion: SqlVersionType;

    //#region: properties in sql v2
    @Metamodel.attribute({
        display: shellStringResources.ModelName_Encrypt,
        association: 'ENCRYPT',
        templatable: true
    })
    public encrypt?: SqlEncryptType;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_TrustServerCertificate,
        association: 'TRUST_SERVER_CERTIFICATE',
        templatable: true
    })
    public trustServerCertificate?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_HostNameInCertificate,
        association: 'HOST_NAME_IN_CERTIFICATE',
        templatable: true
    })
    public hostNameInCertificate?: string;

    public parameterizableAdditionalFields: Array<ParameterConfig<SqlAdditionalPropertiesKeyType>>;
    //#endregion

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    private static validateCredential(linkedService: SqlServerLinkedService, referencedCredential: Credential): IViolation[] | undefined {
        if (linkedService &&
            linkedService.authenticationType === SqlServerAuthType.UserAssignedManagedIdentity &&
            !referencedCredential
        ) {
            return [{
                context: linkedService,
                message: () => shellStringResources.Error_InvalidCredentialReferenced
            }];
        }
        return undefined;
    }
}
